// "My Reports" — the agent's own incidents with their live status.
//
// Freshness is SignalR-first: when the hub is connected, pushes carry the
// updates and polling drops to a slow safety net. The incidents controller is
// rate-limited to 30 req/min per IP, and a polling unit's agents typically share
// one carrier NAT egress, so an unconditional fast poll is a self-inflicted 429.
import { useEffect, useMemo, useRef, useState } from 'react';

import { getUserData } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { type IIncident, INCIDENT_STATUS_LABELS, type IncidentStatus } from '../../../interface/vesr';
import { getRememberedReportedBy, listIncidents } from '../../../lib/vesr-api';
import { useCopy } from '../../utils/hooks';
import TabMessage from '../child-agent/tabs/TabMessage';
import { MegaphoneGlyph, WarnGlyph } from './icons';
import { IncidentCard } from './shared';
import { isMine } from './utils';

type ViewState = 'loading' | 'ready' | 'error';

/** Fallback cadence while the hub is down. */
const POLL_MS = 45_000;
/** Safety net while the hub is connected — pushes are doing the real work. */
const POLL_MS_LIVE = 180_000;
const HIGHLIGHT_MS = 1_800;

interface IMyReportsProps {
	liveIncidents: Record<string, IIncident>;
	/** Hub connection state — drives the polling cadence. */
	liveStatus?: string;
	onCount: (count: number) => void;
	onFileFirst: () => void;
}

const MyReports = ({ liveIncidents, liveStatus, onCount, onFileFirst }: IMyReportsProps) => {
	const { actions } = useGlobalContext();
	const [state, setState] = useState<ViewState>('loading');
	const [reports, setReports] = useState<IIncident[]>([]);
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [highlightIds, setHighlightIds] = useState<Set<string>>(new Set());
	const { copySuccess, action: copiedRef, setAction: setCopiedRef, copy } = useCopy();

	const fullName = getUserData().user?.FullName;
	const rememberedId = getRememberedReportedBy();

	// Latest known status per report — drives the "status changed" toast diff.
	const statusesRef = useRef<Record<string, IncidentStatus>>({});
	const actionsRef = useRef(actions);
	actionsRef.current = actions;

	const load = (silent = false) => {
		if (!silent) setState('loading');
		// Scope server-side where we can. Client-filtering a page of the global
		// feed silently loses an agent's reports once the feed exceeds one page.
		void listIncidents({ pageSize: 50, reportedBy: rememberedId || undefined }).then((out) => {
			if (!out.reachable || out.httpStatus < 200 || out.httpStatus >= 300 || !out.body) {
				if (!silent) setState('error');
				return;
			}
			// Keep the client filter as a belt-and-braces pass: it also matches on
			// name, which covers reports filed before the per-agent identity fix
			// (they carry the old shared reportedBy GUID).
			const mine = (out.body.data ?? []).filter((i) => isMine(i, rememberedId, fullName));
			mine.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
			setReports(mine);
			setState('ready');
		});
	};

	// Initial fetch + poll while mounted + refetch when the tab regains focus.
	// The interval stretches when the hub is connected, because pushes already
	// deliver status changes and the controller is rate-limited per IP.
	const isLive = liveStatus === 'connected';
	useEffect(() => {
		load();
		const interval = setInterval(() => load(true), isLive ? POLL_MS_LIVE : POLL_MS);
		const onVisible = () => {
			if (document.visibilityState === 'visible') load(true);
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			clearInterval(interval);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, [isLive]);

	// Overlay live pushes onto the fetched list (only my own — a state room
	// receives every incident in the state).
	const merged = useMemo(() => {
		const map = new Map(reports.map((r) => [r.id, r]));
		Object.values(liveIncidents).forEach((live) => {
			if (!isMine(live, rememberedId, fullName)) return;
			map.set(live.id, live);
		});
		return [...map.values()].sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
	}, [reports, liveIncidents, rememberedId, fullName]);

	useEffect(() => {
		onCount(merged.length);
	}, [merged.length, onCount]);

	// Status-change detection → one-shot highlight wash + polite toast.
	useEffect(() => {
		const changed: IIncident[] = [];
		merged.forEach((incident) => {
			const prev = statusesRef.current[incident.id];
			if (prev && prev !== incident.status) changed.push(incident);
			statusesRef.current[incident.id] = incident.status;
		});
		if (!changed.length) return;
		setHighlightIds((prevSet) => {
			const next = new Set(prevSet);
			changed.forEach((i) => next.add(i.id));
			return next;
		});
		changed.forEach((incident) => {
			actionsRef.current?.notificationGlobal?.(
				`Report #${incident.referenceId} is now ${INCIDENT_STATUS_LABELS[incident.status] ?? incident.status}`,
				true
			);
		});
		const timer = setTimeout(() => {
			setHighlightIds((prevSet) => {
				const next = new Set(prevSet);
				changed.forEach((i) => next.delete(i.id));
				return next;
			});
		}, HIGHLIGHT_MS);
		return () => clearTimeout(timer);
	}, [merged]);

	if (state === 'loading') {
		return (
			<div className='rh-skeleton f-column-15'>
				<div className='rh-skeleton__card skeleton' />
				<div className='rh-skeleton__card skeleton' />
			</div>
		);
	}

	if (state === 'error') {
		return (
			<TabMessage
				icon={<WarnGlyph />}
				tone='danger'
				title='We couldn’t load your reports.'
				hint='The situation room is temporarily unreachable — your reports are safe.'
				action={{ label: 'Try again', onClick: () => load() }}
			/>
		);
	}

	if (!merged.length) {
		return (
			<TabMessage
				icon={<MegaphoneGlyph />}
				tone='neutral'
				title='No reports yet'
				hint='Situation reports you file will appear here with their live status.'
				action={{ label: 'File a report', onClick: onFileFirst }}
			/>
		);
	}

	return (
		<div
			className='rh-list f-column-15'
			aria-live='polite'
		>
			{merged.map((incident) => (
				<IncidentCard
					key={incident.id}
					incident={incident}
					mine
					expanded={expandedId === incident.id}
					onToggle={() => setExpandedId((prev) => (prev === incident.id ? null : incident.id))}
					highlight={highlightIds.has(incident.id)}
					copied={copySuccess && copiedRef === incident.referenceId}
					onCopyRef={(ref) => {
						setCopiedRef(ref);
						copy(ref);
					}}
				/>
			))}
		</div>
	);
};

export default MyReports;
